// ChannelThreadPanel.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { isAiThreadRootMessage } from 'spaces/utils/aiThreadUtils';

// CSS
import './ChannelThreadPanel.css';

// Components
import ChannelPostForm from 'channels/components/ChannelPostForm';
import Message from 'channels/components/Message';
import MessageFeed from 'channels/components/MessageFeed';
import Button from 'components/button/Button';
import AiThreadRootMessage from 'spaces/components/AiThreadRootMessage';

/**
 * ChannelThreadPanel
 *
 * Renders the desktop thread side panel with root message, replies, and composer.
 *
 * @returns {ReactNode}
 */
const ChannelThreadPanel = React.memo(({
  rootMessage,
  replies,
  loading,
  hasMoreReplies = false,
  onLoadMoreReplies = null,
  errorMessage,
  isSending,
  disabled,
  currentUserKey,
  attachmentNodesByKey,
  scrollToMessageKey = '',
  onClose,
  onOpenAttachmentNode,
  onEditMessage,
  onDeleteMessage,
  onSubmitReply,
  isAiThread = false,
  showAiTyping = false,
  aiPendingCount = 0,
  allowDeleteAnyMessage = false,
  useBackNavigation = false,
  onBack,
  mentionCandidates = [],
  channelMemberKeys = [],
  canInviteMembers = false,
  channelTitle = '',
  membersByKey = {},
  mentionedPeopleByKey = {},
  onInviteMembers,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- HANDLERS ----- //

  const handleClose = onBack || onClose;
  const showBackButton = useBackNavigation;

  // ----- RENDER ----- //

  return (
    <aside
      className="channel-thread-panel"
      style={{
        borderLeft: `1px solid ${theme.backgroundMuted}`,
      }}
    >

      {/* HEADER */}
      <div
        className="channel-thread-panel-header"
      >

        {/* HEADER START */}
        <div className="channel-thread-panel-header-start">
          {showBackButton ? (
            <Button
              iconKey="arrow_back"
              iconColor={theme.foregroundMuted}
              backgroundColor="transparent"
              hoverForegroundColor={theme.foreground}
              iconSize="22"
              height="32px"
              width="32px"
              padding="7px"
              borderRadius="8px"
              onClick={handleClose}
            />
          ) : null}

          {/* TITLE */}
          <div
            className="channel-thread-panel-title"
          >
            Thread
          </div>
        </div>
        {!showBackButton ? (
          <Button
            iconKey="close"
            iconColor={theme.foregroundMuted}
            backgroundColor="transparent"
            hoverForegroundColor={theme.foreground}
            iconSize="16"
            height="32px"
            width="32px"
            padding="7px"
            borderRadius="8px"
            onClick={handleClose}
          />
        ) : null}
      </div>

      {/* ROOT */}
      <div className="channel-thread-panel-root">

        {/* PLACEHOLDER */}
        {rootMessage ? (
          isAiThread && isAiThreadRootMessage(rootMessage) ? (
            <AiThreadRootMessage message={rootMessage} />
          ) : (
            <Message
              message={rootMessage}
              isOwn={currentUserKey === rootMessage?.userKey}
              attachmentNodesByKey={attachmentNodesByKey}
              membersByKey={membersByKey}
              mentionedPeopleByKey={mentionedPeopleByKey}
              isRootMessage
              onOpenAttachmentNode={onOpenAttachmentNode}
            />
          )
        ) : (
          <div
            className="channel-thread-panel-placeholder"
          >
            Loading thread...
          </div>
        )}
      </div>

      {/* DIVIDER */}
      <div
        className="channel-thread-panel-divider"
        role="separator"
      />

      {/* ERROR */}
      {errorMessage ? (
        <div
          className="channel-thread-panel-error"
        >
          {errorMessage}
        </div>
      ) : null}

      {/* FEED */}
      <div className="channel-thread-panel-feed">
        <MessageFeed
          attachmentNodesByKey={attachmentNodesByKey}
          currentUserKey={currentUserKey}
          errorMessage={loading ? errorMessage : ''}
          membersByKey={membersByKey}
          mentionedPeopleByKey={mentionedPeopleByKey}
          messages={replies}
          loading={loading}
          hasMoreMessages={hasMoreReplies}
          onLoadOlderMessages={onLoadMoreReplies}
          loadMoreLabel="Load more replies"
          loadMorePlacement="bottom"
          scrollToMessageKey={scrollToMessageKey}
          emptyPlaceholder="No replies yet. Start the thread."
          showAiTyping={showAiTyping}
          aiPendingCount={aiPendingCount}
          allowDeleteAnyMessage={allowDeleteAnyMessage}
          onDeleteMessage={onDeleteMessage}
          onEditMessage={onEditMessage}
          onOpenAttachmentNode={onOpenAttachmentNode}
        />
      </div>

      <ChannelPostForm
        disabled={disabled}
        isSending={isSending}
        mode="thread"
        isAi={isAiThread}
        mentionCandidates={mentionCandidates}
        channelMemberKeys={channelMemberKeys}
        canInviteMembers={canInviteMembers}
        channelTitle={channelTitle}
        onInviteMembers={onInviteMembers}
        onSubmit={onSubmitReply}
      />
    </aside>
  );
});

// Dev Tools
ChannelThreadPanel.displayName = 'ChannelThreadPanel';

export default ChannelThreadPanel;
